<div class="sales-order-overview-page min-h-full w-full bg-[var(--bg-color)] text-[var(--f-color)]">
  <app-header
    [title]="salesOrderHeaderTitle || ('TITLES.SALES_ORDERS' | translate)"
    [subtitle]="salesOrderSubtitle ?? ''"
  >
    <div header-actions class="hidden lg:block">
      <app-navbar-profile-info></app-navbar-profile-info>
    </div>
  </app-header>

  <div class="relative z-10 mx-auto max-w-6xl px-4 py-6 md:px-8">
    <div class="sales-order-overview-page__topbar">
      <app-back-button [fallbackUrl]="salesOrdersBackUrl" />
    </div>

    @if (terminalBannerKey) {
      <div class="sales-order-overview-page__terminal-banner" role="status" aria-live="polite">
        {{ terminalBannerKey | translate }}
      </div>
    }

    @if (heroActionErrorKey) {
      <div class="sales-order-overview-page__confirm-error" role="alert">
        {{ heroActionErrorKey | translate }}
      </div>
    }

    @if (isLoading) {
      <div class="sales-order-overview-page__overlay">
        <div class="sales-order-overview-page__spinner" aria-hidden="true"></div>
        <span class="sr-only">{{ 'MESSAGES.LOADING' | translate }}</span>
      </div>
    }

    @if (pageErrorKey) {
      <app-card-shell padding="lg">
        <div class="sales-order-overview-page__error-card">
          <h2 class="sales-order-overview-page__error-title">{{ 'ERRORS.UNKNOWN_ERROR' | translate }}</h2>
          <p class="sales-order-overview-page__error-message">{{ pageErrorKey | translate }}</p>
          <button type="button" class="sales-order-overview-page__primary-button" (click)="retry()">
            {{ 'BUTTONS.RETRY' | translate }}
          </button>
        </div>
      </app-card-shell>
    } @else if (salesOrder$ | async; as salesOrder) {
      <div class="flex flex-col gap-6">
        <!-- Hero card -->
        <app-card-shell padding="lg">
          <div class="flex flex-col items-start gap-6 lg:flex-row lg:items-center">
            <!-- Type icon block -->
            @if (heroIcon$ | async; as heroIcon) {
              <div
                class="sales-order-overview-page__hero-icon-block flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-xl text-white shadow-lg"
                [ngClass]="heroIcon.gradientClass"
                aria-hidden="true"
              >
                @switch (heroIcon.iconKey) {
                  @case ('product') {
                    <svg class="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                  }
                  @case ('service') {
                    <svg class="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  }
                  @case ('hybrid') {
                    <svg class="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  }
                  @default {
                    <svg class="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                  }
                }
              </div>
            }

            <!-- Info column -->
            <div class="min-w-0 flex-1">
              <!-- Row 1: number + badges -->
              <div class="mb-1 flex flex-wrap items-center gap-2">
                <h1 class="m-0 text-xl font-bold text-neutral-900 dark:text-neutral-50 md:text-base sales-order-overview-page__hero-title">
                  {{ salesOrder.number || '—' }}
                </h1>

                <span
                  class="sales-order-status-badge"
                  [ngClass]="'sales-order-status-badge--' + statusBadgeKey"
                >
                  <span class="sales-order-status-badge__dot" aria-hidden="true"></span>
                  {{ statusLabelKey | translate }}
                </span>

                @if (isArchived) {
                  <span class="sales-order-overview-page__archived-badge">
                    {{ 'COMPONENTS.SALES_ORDERS.MESSAGES.ARCHIVED_BADGE' | translate }}
                  </span>
                }

                @if (typeBadge$ | async; as typeBadge) {
                  <span
                    class="sales-order-type-badge"
                    [ngClass]="'sales-order-type-badge--' + typeBadge.variant"
                  >
                    {{ typeBadge.labelKey | translate }}
                  </span>
                }

                @if (integrationView$ | async; as integrationView) {
                  <span
                    class="sales-order-overview-page__source-badge"
                    [ngClass]="'sales-order-overview-page__source-badge--' + integrationView.sourceBadgeVariant"
                  >
                    {{ integrationView.sourceBadgeKey | translate }}
                  </span>
                }
              </div>

              <!-- Row 2: short description -->
              <p class="m-0 mb-2 text-sm text-neutral-500 dark:text-neutral-400 sales-order-overview-page__hero-description">
                {{ salesOrderSubtitle || '—' }}
              </p>

              <!-- Row 3: total + created date + updated date -->
              @if (heroView$ | async; as heroView) {
                <div class="flex flex-wrap items-center gap-4 text-sm">
                  <div>
                    <span class="text-neutral-400 dark:text-neutral-500">{{ 'COMPONENTS.SALES_ORDERS.TOTALS.TOTAL' | translate }}:</span>
                    <span class="ml-1 font-bold text-neutral-900 dark:text-neutral-50 sales-order-overview-page__hero-total">{{ heroView.amountIncludingTax | number: '1.2-2' }}</span>
                  </div>
                  <div>
                    <span class="text-neutral-400 dark:text-neutral-500">{{ 'COMPONENTS.SALES_ORDERS.FIELDS.CREATED_DATE' | translate }}:</span>
                    <span class="ml-1 text-neutral-700 dark:text-neutral-300 sales-order-overview-page__hero-created-date">{{ heroView.createdAt ? (heroView.createdAt | date: 'dd.MM.yyyy') : '—' }}</span>
                  </div>
                  <div>
                    <span class="text-neutral-400 dark:text-neutral-500">{{ 'COMPONENTS.GOODS.FIELDS.UPDATED_AT' | translate }}:</span>
                    <span class="ml-1 text-neutral-700 dark:text-neutral-300 sales-order-overview-page__hero-updated-date">{{ heroView.updatedAt ? (heroView.updatedAt | date: 'dd.MM.yyyy') : '—' }}</span>
                  </div>
                </div>
              }
            </div>

            <!-- Action buttons (icon-only with tooltip) -->
            <div class="flex flex-shrink-0 flex-wrap items-center gap-2">
              @if (isArchived) {
                <button
                  type="button"
                  class="sales-order-overview-page__hero-action-btn sales-order-overview-page__hero-action-btn--secondary"
                  [disabled]="isHeroActionInFlight"
                  [title]="'COMPONENTS.SALES_ORDERS.BUTTONS.UNARCHIVE_ORDER' | translate"
                  [attr.aria-label]="'COMPONENTS.SALES_ORDERS.BUTTONS.UNARCHIVE_ORDER' | translate"
                  (click)="unarchiveOrder()"
                >
                  <svg class="sales-order-overview-page__hero-action-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              } @else {
                <button
                  type="button"
                  class="sales-order-overview-page__hero-action-btn sales-order-overview-page__hero-action-btn--secondary"
                  [disabled]="isHeroActionInFlight"
                  [title]="'COMPONENTS.SALES_ORDERS.BUTTONS.ARCHIVE_ORDER' | translate"
                  [attr.aria-label]="'COMPONENTS.SALES_ORDERS.BUTTONS.ARCHIVE_ORDER' | translate"
                  (click)="openArchiveConfirmation()"
                >
                  <svg class="sales-order-overview-page__hero-action-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                  </svg>
                </button>
              }

              @if (canDelete) {
                <button
                  type="button"
                  class="sales-order-overview-page__hero-action-btn sales-order-overview-page__hero-action-btn--danger"
                  [disabled]="isHeroActionInFlight"
                  [title]="'COMPONENTS.SALES_ORDERS.BUTTONS.DELETE_ORDER' | translate"
                  [attr.aria-label]="'COMPONENTS.SALES_ORDERS.BUTTONS.DELETE_ORDER' | translate"
                  (click)="openDeleteConfirmation()"
                >
                  <svg class="sales-order-overview-page__hero-action-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              }
            </div>
          </div>
        </app-card-shell>

        <app-tabs
          class="sales-order-overview-page__tabs"
          [items]="tabs"
          [activeId]="activeTab"
          (activeIdChange)="onTabChange($event)"
        ></app-tabs>

        <div class="sales-order-overview-page__tab-panel">
          @switch (activeTab) {
            @case ('overview') {
              <app-sales-order-overview-tab
                [companyId]="companyId"
                [salesOrderId]="salesOrderId"
                [salesOrder]="salesOrder"
                [clientOptions]="(clientOptions$ | async) ?? []"
                [statusOptions]="statusOptions"
                [integrationView]="integrationView$ | async"
                [isDuplicating]="isDuplicating"
                (orderUpdated)="onOrderUpdated($event)"
                (insufficientStock)="onInsufficientStock($event)"
                (issueInvoiceRequested)="onIssueInvoiceRequested()"
                (createShipmentRequested)="onCreateShipmentRequested()"
                (duplicateOrderRequested)="onDuplicateOrderRequested()"
              />
            }
            @case ('lines') {
              <app-sales-order-lines-tab
                [companyId]="companyId"
                [salesOrderId]="salesOrderId"
                [salesOrder]="salesOrder"
                [unitsOfMeasure]="(unitsOfMeasure$ | async) ?? []"
                [inventoryItemOptions]="(inventoryItemOptions$ | async) ?? []"
                [isReadonly]="salesOrder.status !== 'Draft' && salesOrder.status !== 'New'"
                [insufficientStockLines]="(insufficientStockLines$ | async) ?? []"
                (orderUpdated)="onLinesOrderUpdated($event)"
              />
            }
            @case ('shipments') {
              <app-sales-order-shipments-tab
                [companyId]="companyId"
                [salesOrder]="salesOrder"
                [unitsOfMeasure]="(unitsOfMeasure$ | async) ?? []"
                [customerName]="customerName"
                [disabled]="isTerminalState"
                [refreshToken]="shipmentsRefreshToken"
                (shipmentCreated)="onShipmentCreated()"
                (shipmentUpdated)="onShipmentCreated()"
                (createShipmentRequested)="onCreateShipmentRequested()"
              />
            }
            @case ('finance') {
              <app-sales-order-finance-tab
                [companyId]="companyId"
                [salesOrder]="salesOrder"
                [unitsOfMeasure]="(unitsOfMeasure$ | async) ?? []"
                [customerName]="customerName"
                [disabled]="isTerminalState"
                [refreshToken]="financeRefreshToken"
                (invoiceCreated)="onInvoiceCreated()"
                (createInvoiceRequested)="onIssueInvoiceRequested()"
              />
            }
            @case ('integration') {
              <app-sales-order-integration-tab [integrationView]="integrationView$ | async" />
            }
            @case ('history') {
              <app-sales-order-history-tab
                [companyId]="companyId"
                [salesOrderId]="salesOrderId"
                [refreshToken]="historyRefreshToken"
                [isActive]="activeTab === 'history'"
              />
            }
          }
        </div>
      </div>

      <!--
        Single create-invoice / create-shipment modal mounts that serve every entry point:
        the Overview tab quick actions AND the in-tab "+ NEW" buttons on the Finance /
        Shipments tabs (which now emit `createInvoiceRequested` / `createShipmentRequested`
        outputs that the parent funnels through `onIssueInvoiceRequested` /
        `onCreateShipmentRequested`). Mounting them here at the page-content level keeps
        the backdrop scoped to the content area and eliminates the visual drift the
        previous dual-mount design exhibited between the two entry points.
      -->
      <app-sales-order-create-invoice-modal
        [companyId]="companyId"
        [salesOrder]="salesOrder"
        [unitsOfMeasure]="(unitsOfMeasure$ | async) ?? []"
        [customerName]="customerName"
        [isOpen]="isCreateInvoiceModalOpen"
        (closed)="closeCreateInvoiceModal()"
        (invoiceCreated)="onInvoiceCreatedFromQuickAction()"
      ></app-sales-order-create-invoice-modal>

      <app-sales-order-create-shipment-modal
        [companyId]="companyId"
        [salesOrder]="salesOrder"
        [unitsOfMeasure]="(unitsOfMeasure$ | async) ?? []"
        [inventoryItemOptions]="(inventoryItemOptions$ | async) ?? []"
        [customerName]="customerName"
        [isOpen]="isCreateShipmentModalOpen"
        (closed)="closeCreateShipmentModal()"
        (shipmentCreated)="onShipmentCreatedFromQuickAction()"
      ></app-sales-order-create-shipment-modal>
    }

    <app-reusable-modal
      [isOpen]="isConfirmationOpen"
      [panelClass]="'max-w-lg'"
      [contentScoped]="true"
      [title]="confirmationTitleKey | translate"
      (closed)="closeHeroConfirmation()"
    >
      <section class="sales-order-overview-page__hero-confirmation">
        <p class="sales-order-overview-page__hero-confirmation-prompt">
          {{ confirmationPromptKey | translate }}
        </p>

        <div class="sales-order-overview-page__hero-confirmation-actions">
          <button
            type="button"
            class="form-secondary-btn"
            [disabled]="isHeroActionInFlight"
            (click)="closeHeroConfirmation()"
          >
            {{ 'BUTTONS.BACK' | translate }}
          </button>
          <button
            type="button"
            class="sales-order-overview-page__hero-confirmation-danger-btn"
            [disabled]="isHeroActionInFlight"
            (click)="confirmHeroAction()"
          >
            @if (isHeroActionInFlight) {
              {{ confirmationLoadingKey | translate }}
            } @else {
              {{ confirmationActionKey | translate }}
            }
          </button>
        </div>
      </section>
    </app-reusable-modal>
  </div>
</div>
